import * as tokenService from 'services/token-service';
import * as tokenHelper from 'helpers/token-helper';
import * as tokenApi from 'api/token-api';
import { Jwt } from 'types/jwt';
import { LatestTermsOfUse } from 'types/latest-terms-of-use';

const SESSION_TIMEOUT_MS = 1230000;
const TIMEOUT_BUFFER_MS = 30000;
const REFRESH_LAST_ACTION_BUFFER = 30000;

let sessionTimeout: number;
let tokenTimestamp: number;
let tokenRefreshTimer: NodeJS.Timer;
let sessionTimeoutFunctionExecutionTime: number;
let sessionTimeoutFunction: Function = () => {
  return;
};
let termsFillerFunction: (optelTerms: LatestTermsOfUse, nmvsTerms: LatestTermsOfUse) => void = () => {
  return;
};

function setTokenRefreshTimer(timeToCheckTimeout: number) {
  sessionTimeoutFunctionExecutionTime = timeToCheckTimeout + Date.now();
  clearTimeout(tokenRefreshTimer);
  tokenRefreshTimer = setTimeout(() => {
    sessionTimeoutHandler();
  }, timeToCheckTimeout);
}

async function sessionTimeoutHandler() {
  try {
    const lastActionTimestamp = await tokenService.readLastActionTimestamp();
    if (lastActionTimestamp + sessionTimeout - TIMEOUT_BUFFER_MS < Date.now()) {
      sessionTimeoutFunction();
    } else {
      await refreshSessionToken(true, termsFillerFunction);
    }
  } catch (e) {
    sessionTimeoutFunction();
  }
}

export async function renewSession(token: string, callback: Function): Promise<Jwt> {
  await tokenService.storeToken(tokenService.JWT_SESSION_TOKEN, token);
  const jwt = await parseToken(token);
  sessionTimeoutFunction = callback;
  clearTimeout(tokenRefreshTimer);
  setTokenRefreshTimer(sessionTimeout - TIMEOUT_BUFFER_MS);
  return jwt;
}

export async function removeSession() {
  await tokenService.removeSessionToken();
  clearTimeout(tokenRefreshTimer);
}

export async function refreshLastAction() {
  const token = await tokenService.readToken(tokenService.JWT_SESSION_TOKEN);
  // if timer is already set do not set it again, or if session timeout function will not execute within buffer time
  if ((!tokenRefreshTimer && token) || (sessionTimeoutFunctionExecutionTime - Date.now() >= REFRESH_LAST_ACTION_BUFFER)) {
    // set timer for 30 sec to refresh token
    setTokenRefreshTimer(REFRESH_LAST_ACTION_BUFFER);
  }
  await tokenService.storeLastAction(Date.now());
}

export async function refreshSessionToken(isOnline: boolean,
  termsFiller: (optelTerms: LatestTermsOfUse, nmvsTerms: LatestTermsOfUse) => void): Promise<Jwt> {
  const token = await tokenService.readToken(tokenService.JWT_SESSION_TOKEN);
  termsFillerFunction = termsFiller;
  if (isOnline === false) {
    const jwt = await parseToken(token);
    await refreshLastAction();
    return jwt;
  } else {
    const tokenResponse = await tokenApi.refreshToken(token);
    termsFiller(tokenResponse.optelTermsOfUse, tokenResponse.nmvsTermsOfUse);
    return await renewSession(tokenResponse.userToken, sessionTimeoutFunction);
  }
}

export function setOfflineSessionTimeout() {
  sessionTimeout = SESSION_TIMEOUT_MS;
  tokenTimestamp = Date.now() * 1000;
}

export async function isSessionTokenValid(isOnline: boolean): Promise<boolean> {
  try {
    const token = await tokenService.readToken(tokenService.JWT_SESSION_TOKEN);
    if (!token) {
      return false;
    }

    const jwt = tokenHelper.decodeToken(token);

    if (isOnline === false) {
      const lastAction = await tokenService.readLastActionTimestamp();
      const timeoutDelay = (jwt.exp - jwt.iat) * 1000;
      const timeSinceLastAction = Date.now() - lastAction;
      return timeoutDelay >= timeSinceLastAction;
    } else {
      const expiration = jwt.exp * 1000 - Date.now();
      return expiration >= 0;
    }
  } catch (e) {
    return false;
  }
}

async function parseToken(token: string): Promise<Jwt> {
  const jwt = tokenHelper.decodeToken(token);
  // tokenTimestamp = jwt.iat * 1000;
  // sessionTimeout = jwt.exp * 1000 - tokenTimestamp;  
  setOfflineSessionTimeout();
  await tokenService.storeLastAction(tokenTimestamp);
  return jwt;
}


// WEBPACK FOOTER //
// ./src/services/session-timeout-service.ts